//AuthService

// services/auth/AuthService.js - Updated to include Authorization header
import axios from 'axios';
import { AUTH_ENDPOINTS, COGNITO_CONFIG } from '../../config/Index';
import { tokenService } from './tokenService';
import { asyncStorage } from '../storage/asyncStorageService';

// Create authentication axios instance
const authInstance = axios.create({
  baseURL: AUTH_ENDPOINTS.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

// List of auth endpoints that don't need user auth headers
const PUBLIC_AUTH_ENDPOINTS = [
  '/generate/otp',
  '/login',
  '/register/user',
  '/cognito/callback',
  '/accept-invite'
];

const needsUserAuthHeaders = (url) => {
  // Only some auth endpoints need user auth headers (like getting user organizations)
  return !PUBLIC_AUTH_ENDPOINTS.some(endpoint => url?.includes(endpoint));
};

// Add auth interceptor to auth instance
authInstance.interceptors.request.use(async (config) => {
  // Only add user auth headers for endpoints that need them
  if (needsUserAuthHeaders(config.url)) {
    try {
      const authHeaders = await tokenService.getAuthHeaders();
      // Merge auth headers which include Authorization and X-Auth-Token
      config.headers = { ...config.headers, ...authHeaders };
    } catch (error) {
    }
  }

  return config;
});

// Add response interceptor for error handling
authInstance.interceptors.response.use(
  response => response,
  error => {
    // Return error for handling by calling code
    return Promise.reject(error);
  }
);

class AuthService {
  /**
   * Generate OTP for email login
   */
 async generateOtp(data, isResend = false) {
  try {
    const requestBody = {
      email: data.email.toLowerCase()
    };

    // Add metadata to indicate this is a resend request
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };

    // If this is a resend, add clientMetadata to indicate so
    if (isResend) {
      requestBody.clientMetadata = {
        action: 'resend'
      };
    }

    const response = await authInstance.post('/generate/otp', requestBody, config);
    return response.data;
  } catch (error) {
    throw this.handleError(error);
  }
}

  /**
   * Login with email and OTP
   */
  async login(loginData) {
    try {
      const response = await authInstance.post('/login', {
        email: loginData.email.toLowerCase(),
        otp: loginData.otp,
        session: loginData.session
      });
      
      if (response.data?.accessToken) {
        const { accessToken, idToken, refreshToken } = response.data;
        tokenService.setTokens(accessToken, idToken || null, refreshToken || null);
        tokenService.setEmail(response.data.email || loginData.email);
      }

      return {
        ...response.data,
        success: true
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  }

  /**
   * Register new user
   */
  async register(registrationData) {
    try {
      const response = await authInstance.post('/register/user', {
        name: registrationData.name?.trim(),
        surname: registrationData.surname?.trim(),
        email: registrationData.email?.toLowerCase().trim(),
        organisation: registrationData.organization?.trim(),
        termsAccepted: registrationData.termsAccepted ?? false
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return this.handleRegistrationError(error);
    }
  }

  /**
   * Verify OTP for email verification during registration
   */
  async verifyOTP(data) {
    try {
      const response = await authInstance.post('/verify-otp', {
        email: data.email?.toLowerCase().trim(),
        verificationCode: data.verificationCode?.trim()
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        data: error.response?.data || { message: 'Verification failed. Please try again.' }
      };
    }
  }

  /**
   * Accept invitation
   */
  async acceptInvite(data) {
    try {
      const response = await authInstance.post('/accept-invite', data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to accept invitation'
      };
    }
  }

  /**
   * Handle Cognito callback for Microsoft/Google login
   */
  async handleCognitoCallback(code, emailHint, clientId, state) {
    try {
      const response = await authInstance.post('/cognito/callback', {
        code,
        emailHint: emailHint || "",
        clientId: clientId || "",
        state: state || ""
      });
      
      if (response.data?.accessToken) {
        // Store tokens
        tokenService.setToken(response.data.accessToken);
        if (response.data.idToken) {
          tokenService.setTokens(response.data.accessToken, response.data.idToken);
        }
        
        // Store email
        if (response.data.email) {
          tokenService.setEmail(response.data.email);
        }
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: this.getCognitoErrorMessage(error)
      };
    }
  }

  /**
   * Get user organizations
   */
  async getUserOrganizations(email, token) {
    try {
      // Include both Authorization and X-Auth-Token headers
      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Auth-Token': `Bearer ${token}`
      };

      const response = await authInstance.get(AUTH_ENDPOINTS.getUserOrganizations(email), { headers });
      return { success: true, data: response.data || [] };
    } catch (error) {
      return { success: true, data: [] };
    }
  }

  /**
   * Get client info for domain
   */
  async fetchClientInfoForDomain(domain) {
    try {
      const response = await authInstance.get(AUTH_ENDPOINTS.DOMAIN_CLIENT, {
        params: { domain }
      });

      if (response.data?.clientId) {
        return {
          clientId: response.data.clientId,
          redirectUri: response.data.redirectUri || COGNITO_CONFIG.DEFAULT_REDIRECT_URI
        };
      }

      return {
        clientId: COGNITO_CONFIG.DEFAULT_CLIENT_ID,
        redirectUri: COGNITO_CONFIG.DEFAULT_REDIRECT_URI
      };
    } catch (error) {
      return {
        clientId: COGNITO_CONFIG.DEFAULT_CLIENT_ID,
        redirectUri: COGNITO_CONFIG.DEFAULT_REDIRECT_URI
      };
    }
  }

  /**
   * Handle registration errors
   */
  handleRegistrationError(error) {
    if (error.response?.status === 409) {
      return {
        success: false,
        error: 'Email address already registered. Please log in instead.',
        status: 409
      };
    }

    return {
      success: false,
      error: 'Registration failed. Please try again.',
      status: error.response?.status || 500
    };
  }

  /**
   * Get Cognito error message
   */
  getCognitoErrorMessage(error) {
    if (error.response?.status === 500) {
      return 'User not registered/found';
    }
    
    const errorMessage = error.response?.data?.message || error.message;
    if (errorMessage?.includes('User not found')) {
      return 'User not registered/found';
    }
    
    if (errorMessage?.includes('email domain')) {
      return 'Your email domain is not authorized. Please contact your administrator.';
    }
    
    return 'Authentication failed. Please try again.';
  }

  /**
   * Handle general errors
   */
  handleError(error) {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    const newError = new Error(message);
    newError.status = error.response?.status;
    return newError;
  }

  /**
   * Logout user
   */
  logout() {
    tokenService.clearAll();
    asyncStorage.remove('userData');
  }
}

// Create singleton instance
export const authService = new AuthService();